<template>
  <div
    :id="groupId"
    ref="radioGroupRef"
    :class="groupClass"
    role="radiogroup"
    :aria-label="!isLabeledByFormItem ? label || 'radio-group' : undefined"
    :aria-labelledby="isLabeledByFormItem ? formItem!.labelId : undefined"
  >
    <span
      v-if="segmenter"
      :class="ns.be('group', 'segmenter-dot')"
      :style="segmenterDotStyle"
    />
    <slot />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  provide,
  reactive,
  ref,
  toRefs,
  watch,
  watchEffect,
} from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { useFormItem, useFormItemInputId } from '@element-plus/components/form'
import { UPDATE_MODEL_EVENT } from '@element-plus/constants'
import { useId, useNamespace } from '@element-plus/hooks'
import { debugWarn } from '@element-plus/utils'
import { radioGroupEmits, radioGroupProps } from './radio-group'
import { radioGroupKey } from './constants'

import type { RadioGroupProps } from './radio-group'
import type { UseResizeObserverReturn } from '@vueuse/core'

defineOptions({
  name: 'ElRadioGroup',
})

const props = defineProps(radioGroupProps)
const emit = defineEmits(radioGroupEmits)

const ns = useNamespace('radio')
const radioId = useId()
const radioGroupRef = ref<HTMLDivElement>()
let resizeStopper: UseResizeObserverReturn['stop'] | undefined = undefined
const { formItem } = useFormItem()
const { inputId: groupId, isLabeledByFormItem } = useFormItemInputId(props, {
  formItemContext: formItem,
})

const updateSegmenter = (value: RadioGroupProps['modelValue']) => {
  const radios =
    radioGroupRef.value!.querySelectorAll<HTMLInputElement>('[type=radio]')
  const checked = Array.from(radios).find((radio) => radio.value === value)
  if (checked) {
    const rect = checked.parentElement!.getBoundingClientRect()
    const containerRect = radioGroupRef.value!.getBoundingClientRect()
    // absolute
    segmenterDotStyle.left = `${rect.left - containerRect.left}px`
    segmenterDotStyle.top = `${rect.top - containerRect.top}px`
    segmenterDotStyle.width = `${rect.width}px`
    segmenterDotStyle.height = `${rect.height}px`
  }
}

const resizeHandler = (): void => {
  if (props.modelValue) {
    updateSegmenter(props.modelValue)
  }
}

const changeEvent = (value: RadioGroupProps['modelValue']) => {
  if (props.segmenter) {
    updateSegmenter(value)
  }
  emit(UPDATE_MODEL_EVENT, value)
  nextTick(() => emit('change', value))
}

onMounted(() => {
  const radios =
    radioGroupRef.value!.querySelectorAll<HTMLInputElement>('[type=radio]')
  const firstLabel = radios[0]
  if (!Array.from(radios).some((radio) => radio.checked) && firstLabel) {
    firstLabel.tabIndex = 0
  }

  if (props.segmenter && props.modelValue) {
    updateSegmenter(props.modelValue)
  }

  watchEffect(() => {
    if (props.segmenter)
      resizeStopper = useResizeObserver(radioGroupRef, resizeHandler).stop
    else resizeStopper?.()
  })
})

const name = computed(() => {
  return props.name || radioId.value
})

const groupClass = computed(() => {
  return [
    ns.b('group'),
    props.disabled && ns.bm('group', 'disabled'),
    props.size && props.size !== 'default' && ns.bm('group', props.size),
    props.segmenter && ns.bm('group', 'segmenter'),
    props.segmenter &&
      props.segmenterDivider &&
      ns.bm('group', 'segmenter-divider'),
    props.segmenter &&
      props.segmenterType !== 'default' &&
      ns.bm('group', `segmenter-${props.segmenterType}`),
  ]
})

const segmenterDotStyle = reactive({
  left: '0px',
  top: '0px',
  width: '0px',
  height: '0px',
})

provide(
  radioGroupKey,
  reactive({
    ...toRefs(props),
    changeEvent,
    name,
  })
)

watch(
  () => props.modelValue,
  () => {
    if (props.validateEvent) {
      formItem?.validate('change').catch((err) => debugWarn(err))
    }
  }
)
</script>
